import { ConversationMessage } from '@provision/entity-types';
import { ApolloClient } from '@provision/graphql-client';
import { WebSocketEventBus } from '@provision/ui-real-time-events';
import { useEffect } from 'react';
import { debounceTime, groupBy, mergeMap } from 'rxjs';
import { getCachedConversation } from 'src/graphql/entities/conversation/conversation';
import {
  GET_CONVERSATION_AGGREGATIONS_QUERY,
  GetConversationAggregationsQuery,
  modifyConversationAggregationsCacheItem,
} from 'src/graphql/entities/conversation-aggregations';
import { useCurrentUserProfile } from 'src/graphql/entities/platform-user/platform-user';

const DEBOUNCE_AGGREGATION_LIMIT_MS = 5000;

export const useConversationMessageSubscription = (
  wsEventBus: WebSocketEventBus,
  apolloClient: ApolloClient<unknown>,
) => {
  const { user } = useCurrentUserProfile();

  useEffect(() => {
    const subscription = wsEventBus
      .onEntity<ConversationMessage>({
        name: 'ConversationMessage',
        operation: 'create',
      })
      .pipe(
        groupBy((evt) => String(evt.attributes.conversationId)),
        mergeMap((group$) => group$.pipe(debounceTime(DEBOUNCE_AGGREGATION_LIMIT_MS))),
      )
      .subscribe({
        next: (event) =>
          queryAndUpdateAggregations(apolloClient, event.attributes, user?.id ?? ''),
      });

    return () => {
      subscription.unsubscribe();
    };
  }, [wsEventBus, user?.id, apolloClient]);
};

export function queryAndUpdateAggregations(
  client: ApolloClient<unknown>,
  conversationMessage: ConversationMessage,
  userId: string,
) {
  const { conversationId, isPrivate } = conversationMessage;

  if (isPrivate) {
    return;
  }

  const conversationFromCache = getCachedConversation(client.cache, conversationId);

  if (!conversationFromCache) {
    return;
  }

  void client
    .query<GetConversationAggregationsQuery>({
      query: GET_CONVERSATION_AGGREGATIONS_QUERY,
      variables: {
        input: {
          userId,
          conversationIds: [conversationId],
        },
      },
      context: {
        ttl: 0,
      },
      fetchPolicy: 'no-cache',
    })
    .then((result) => {
      const updatedItem = result.data?.getConversationAggregations.records[0];

      if (!updatedItem) {
        return;
      }

      return modifyConversationAggregationsCacheItem(
        client.cache,
        conversationId,
        updatedItem,
      );
    })
    .catch((e) => console.error(e));
}
